'use strict';
/**
 * When the button is clicked, this will attempt to fire off the referenced function in the same fashion as ng-click.
 * Additionally, this disables the input, and displays a message when a reponse is received.
 * This directive only requires one attribute, `inline-message`, whose value is optional,
 * but can be used to target a specific location for the message to appear. If left blank, the message will be the
 * last sibling of this directive.
 *
 * The other attributes allow custom messages to be displayed on success or failure.
 *
 * This directive actually works just fine with `ng-click` (read: looks like it works just fine), but if you want to
 * prevent click spamming, use the `click-action` attribute instead. This prevents click spamming while the input is
 * waiting for a server response, and also ties the animation to the request.
 * Otherwise, the animation will just run immediately. Like I said, it looks like it works just fine.
 *
 * A few known issues:
 * - If the message takes up two lines, the animations kinda break, so you may need to override some of the CSS to coax
 *   the message onto one line. Or just choose a shorter message. You know what? Just do that. Less is more.
 * - There may be other scenarios where you need to overwrite the default CSS. Just target `.inline-msg` to position it
 *   where you want it, and `.smokescreen` to match the background color.
 * - If this directive encounters an error, it will display a different message, but its parent $scope may need to
 *   deal with the error, as well. For example, on a checkbox input, the data model in the parent $scope will need to be
 *   reverted to reflect its state before the user's input.
 */

angular.module('msUtility').directive('inlineMessage', ['$parse', '$q', '$timeout', '$log', '$compile', '$translate', function ($parse, $q, $timeout, $log, $compile, $translate) {
  return {
    restrict: 'AE',
    scope: {
      inlineMessage: '@',
      superfluousAnimations: '@',
      successMessage: '@',
      errorMessage: '@',
      clickAction: '&'
    },
    link: function link(scope, element, attr) {
      scope.success = false;
      scope.error = false;
      scope.pending = false;
      var successMessageText = scope.successMessage || $translate.instant('common.actions.saved');
      var errorMessageText = scope.errorMessage || $translate.instant('common.actions.error');
      // We are double translating this string to resolve issue where if `?isMobileWebView`
      // - user would see messageToDisplay as the JSON path of the translation key
      var messageElement = $compile('<div class="inline-msg" ng-class="{success: success, error: error}">' + '<span>{{ messageToDisplay | translate }}</span>' + '<span class="smokescreen"></span></div>')(scope);
      var smokescreen = angular.element(messageElement.children('.smokescreen')[0]);

      // If there was no value set, make the message a sibling of this element.
      if (scope.inlineMessage === '') {
        element.siblings().last().after(messageElement);
      }
      // If the value is "false" then, don't display a message.
      else if (!scope.$eval(scope.inlineMessage)) {
          $log.debug('inlineMessage: No message will be displayed.');
          messageElement = angular.element('<span class="inline-msg" style="margin:0;position:relative;top:0;left:0;"></span>');
        }
        // If the string matches one of this element's children, add the message after it.
        else if (element.find(scope.inlineMessage).length === 1) {
            $log.debug('inlineMessage: Placing message after this elements child.');
            element.find(scope.inlineMessage).after(messageElement);
          }
          // If the string doesn't match a sibling, but it is not empty, just append it to this element.
          else {
              $log.warn('inlineMessage: No specific children found, so the message will be the last child of this element.');
              element.append(messageElement);
            }

      var burst = new mojs.Burst({
        parent: messageElement[0],
        duration: 1500,
        delay: 400,
        shape: 'circle',
        fill: '#4caf50',
        x: 0,
        y: '50%',
        opacity: 0.6,
        childOptions: { radius: { 6: 0 } },
        radius: { 4: 20 },
        count: 6,
        isSwirl: true,
        isRunLess: true,
        easing: mojs.easing.bezier(0.1, 1, 0.3, 1)
      });

      var moveScreen = new mojs.Tween({
        duration: 750,
        onStart: function onStart() {
          smokescreen.css('width', '100%');
        },
        onUpdate: function onUpdate(progress) {
          var travelDistance = smokescreen.width();
          var easeProgress = mojs.easing.ease.out(progress);
          smokescreen.css('width', travelDistance - travelDistance * easeProgress);
        }
      });

      element.on('click', function (event) {
        if (scope.$eval(attr.ngDisabled)) {
          event.preventDefault();
          $log.debug('inlineMessage: This input is disabled by the `ng-disabled` attribute.');
          return;
        }
        if (scope.pending) {
          event.preventDefault();
          $log.debug('inlineMessage: This input is disabled because it is currently waiting for a response from the server.');
          return;
        }

        messageElement.removeClass('visible');
        scope.pending = true;

        scope.$apply(function () {
          // $q.when() is used in case the
          // clickAction function does not return a promise
          $q.when(scope.clickAction(scope, {
            $event: event
          })).then(function () {
            scope.success = true;
            scope.messageToDisplay = successMessageText;

            if (angular.isDefined(scope.superfluousAnimations)) {
              $timeout(function () {
                // We calculate the position of the burst after Angular does its thing and updates the text.
                burst.props.x = messageElement.width() + 30 + 'px';
                if (scope.$eval(scope.inlineMessage)) {
                  // We remake the burst here because I wasn't able to change the parent property after it was created.
                  // Maybe when they add better documentation, this can be cleaned up.
                  burst = new mojs.Burst({
                    parent: element[0],
                    duration: 1500,
                    delay: 0,
                    shape: 'circle',
                    fill: '#4caf50',
                    x: event.originalEvent.layerX - 25 + 'px',
                    y: event.originalEvent.layerY - 5 + 'px',
                    opacity: 0.6,
                    childOptions: { radius: { 6: 0 } },
                    radius: { 4: 20 },
                    count: 6,
                    isSwirl: true,
                    isRunLess: true,
                    easing: mojs.easing.bezier(0.1, 1, 0.3, 1)
                  });
                }
                burst.run();
              });
            }

            messageElement.addClass('visible');
            moveScreen.run();

            $timeout(function () {
              messageElement.removeClass('visible');
              scope.pending = false;
              scope.success = false;
            }, 1750);
          }, function (err) {
            scope.error = true;
            scope.messageToDisplay = errorMessageText;

            moveScreen.run();
            messageElement.addClass('visible');

            $timeout(function () {
              scope.pending = false;
              scope.error = false;
              messageElement.removeClass('visible');
            }, 1750);

            return err;
          });
        });
      });
    }
  };
}]);